// All visitor-facing copy for the Halloween quiz, in English, Spanish and Dutch.
// Keys are shared; {placeholders} are filled by t(). Missing keys fall back to English.
export const LANGS = ['en', 'es', 'nl'];
export const LANG_NAMES = { en: 'English', es: 'Español', nl: 'Nederlands' };
export const LOCALES = { en: 'en-GB', es: 'es-ES', nl: 'nl-NL' };

const en = {
  brandLine: 'Latin Halloween Festival',
  skipToContent: 'Skip to content',
  loading: 'Loading…',
  retry: 'Try again',
  back: 'Back',
  languageLabel: 'Language',
  lang: {
    title: 'Choose your language',
    intro: 'You can switch at any time without losing your progress.',
    suggested: 'Suggested for your browser',
    continue: 'Continue in {name}',
  },
  rules: {
    kicker: 'Baila Dembow Halloween quiz',
    headline: 'Five questions. One Halloween reward. Unlock 5%, 10%, or 15% off your ticket.',
    howTitle: 'How it works',
    r1: 'You get five questions from one randomly assigned theme: Halloween movies, Halloween facts, spell puzzles, or Baila Dembow and music.',
    r2: 'Each correct answer is one point. Your discount depends on your score:',
    tier5: '0–2 correct',
    tier10: '3–4 correct',
    tier15: '5 correct',
    tierResult: '{tier}% off',
    r3: 'Play first. When you see your score, enter your details and confirm your email to receive the code. One code per email address.',
    r4: 'Answers are locked and scored on our server. Correct answers are not shown during the quiz.',
    r5: 'Your discount code is sent by email only. It is never shown on this page.',
    r5b: 'The code is valid until {date}.',
    r5c: 'Each code is valid for {hours} hours after it is sent, so use it quickly.',
    r6: 'You must be 18 or older. Promotional emails are optional and not needed to take part or to get your discount.',
    r7: 'Different email addresses can still belong to the same person; the one-attempt rule applies per verified email address.',
    cta: 'Start the challenge',
    starting: 'Dealing your questions…',
    time: 'Takes about two minutes.',
    terms: 'Event terms',
  },
  form: {
    title: 'Who is playing?',
    why: 'We use these details to run the quiz, send your reward and understand who takes part. Read the privacy notice for the full picture.',
    fullName: 'Full name',
    email: 'Email address',
    age: 'Age',
    ageHelp: 'Whole years. You must be 18 or older.',
    gender: 'Sex / gender',
    genderPick: 'Select an option',
    genderSelf: 'Please describe',
    city: 'City',
    country: 'Country',
    countryPick: 'Select your country',
    accept: 'I accept the quiz rules and have read the {privacy}.',
    privacy: 'privacy notice',
    terms: 'event terms',
    marketingLegend: 'Optional',
    submit: 'Send me my code',
    sending: 'Sending…',
    summary: 'Please fix the highlighted fields.',
    genders: { woman: 'Woman', man: 'Man', non_binary: 'Non-binary', self_describe: 'I describe myself', prefer_not_to_say: 'Prefer not to say' },
    err: {
      required: 'Please fill this in.',
      invalid: 'This does not look right.',
      under_18: 'You must be 18 or older to take part.',
      acceptTerms: 'Please accept to continue.',
    },
  },
  verify: {
    title: 'Check your inbox',
    body: 'We sent a link and a 6-digit code to {email}. Open the link, or enter the code below.',
    hint: 'No email? Check your spam folder. The link and code work once and expire in {minutes} minutes.',
    codeLabel: '6-digit code',
    submit: 'Verify email',
    verifying: 'Verifying…',
    resend: 'Send a new email',
    resent: 'A new email is on its way.',
    edit: 'Wrong address? Edit my details',
    linkTitle: 'Verifying your email…',
    linkFailed: 'That link is invalid or has expired.',
    linkFailedHelp: 'Enter your email address and we will send a new one.',
    emailLabel: 'Email address',
  },
  ready: {
    title: 'Email confirmed',
    body: 'Your five questions are ready. Once you lock in an answer you cannot change it.',
    cta: 'Start the quiz',
  },
  quiz: {
    progress: 'Question {n} of {total}',
    progressLabel: 'Quiz progress',
    theme: 'Theme',
    themes: { movies: 'Halloween movies', facts: 'Halloween facts', spells: 'Cast-a-spell puzzles', music: 'Baila Dembow and music' },
    lock: 'Lock in my answer',
    pick: 'Pick an answer to continue',
    saving: 'Saving your answer…',
    resumed: 'Welcome back. Your progress was saved.',
    answerGroup: 'Answers',
  },
  result: {
    title: 'Quiz complete',
    claimKicker: 'Your reward',
    claimTitle: 'Get your {tier}% code',
    claimIntro: 'Enter your details and confirm your email. The code goes to that address within a minute.',
    pendingTitle: 'One more step',
    pendingBody: 'We sent a link and a 6-digit code to {email}. Open the link, or enter the code below, and your discount code follows right away.',
    alreadyTitle: 'You already have a code',
    score: 'You scored {score} out of {total}',
    earned: 'You earned {tier}% off your Halloween ticket',
    codeNote: 'We never show your code on this page. It is sent by email only.',
    valid: 'Valid until {date} (Amsterdam time). Restrictions are listed in the email.',
    validPersonal: 'Your code expires on {date} (Amsterdam time). Use it before then.',
    emailTitle: 'Your code',
    mail: {
      none: 'We are preparing your email.',
      queued: 'We are sending your code. This can take a minute. If it fails we retry automatically.',
      sending: 'We are sending your code.',
      accepted: 'Your email has been handed to our email provider. Check your inbox and spam folder.',
      delivered: 'Delivered. Check your inbox, and your spam folder if you cannot see it.',
      delayed: 'Delivery is delayed. It should arrive soon.',
      bounced: 'The email bounced. Use the button below to send it again. If it keeps failing, the team has been notified.',
      complained: 'The email was marked as spam. Use the button below to send it again.',
      failed: 'We could not send the email yet. Your reward is safe. Use the button below to try again.',
      waiting_for_inventory: 'Your reward is reserved. Codes for your tier are being topped up and your email will follow as soon as yours is ready. You do not need to retake the quiz.',
    },
    resend: 'Send my code again',
    resendDone: 'We sent the same code again.',
    resendWait: 'Please wait a bit before asking again.',
    tickets: 'Open the ticket shop',
    ticketsHelp: 'Use the link in your email so the code is applied at checkout.',
    already: 'This email address has already completed the quiz. You can request your reward email again, but the quiz cannot be replayed.',
    review: 'Your answers',
    reviewCorrect: 'Correct answer',
    reviewYours: 'Your answer',
  },
  closed: {
    disabled: 'The quiz is not available right now.',
    not_open: 'The quiz opens on {date}.',
    closed: 'This campaign has ended. Thanks for playing.',
    tickets: 'See the Halloween event',
  },
  music: { play: 'Play music', mute: 'Mute music', unmute: 'Unmute music', label: 'Music' },
  errors: {
    network: 'We could not reach the server. Check your connection and try again.',
    server: 'Something went wrong on our side. Please try again in a moment.',
    not_configured: 'The quiz is temporarily unavailable. Please try again later.',
    rate_limited: 'Too many attempts. Please try again in {minutes} min.',
    invalid_or_expired: 'That code or link is invalid or has expired. Send a new email and try again.',
    email_unavailable: 'We could not send the email right now. Please try again in a moment.',
    no_session: 'Your session ended. Please verify your email again.',
    campaign_unavailable: 'The quiz is not available right now.',
    question_bank_incomplete: 'The quiz is not ready yet. Please try again later.',
    generic: 'Something went wrong. Please try again.',
  },
  footer: { terms: 'Terms', privacy: 'Privacy', cookies: 'Cookie settings', hello: 'Back to the Halloween event' },
  unsub: { title: 'Unsubscribe', body: 'Confirm that you no longer want promotional emails from Baila Dembow.', cta: 'Unsubscribe me', done: 'You are unsubscribed. You will not receive promotional emails.', failed: 'This unsubscribe link is not valid.' },
};

const es = {
  brandLine: 'Latin Halloween Festival',
  skipToContent: 'Saltar al contenido',
  loading: 'Cargando…',
  retry: 'Intentar de nuevo',
  back: 'Atrás',
  languageLabel: 'Idioma',
  lang: {
    title: 'Elige tu idioma',
    intro: 'Puedes cambiarlo en cualquier momento sin perder tu progreso.',
    suggested: 'Sugerido para tu navegador',
    continue: 'Continuar en {name}',
  },
  rules: {
    kicker: 'Quiz de Halloween de Baila Dembow',
    headline: 'Cinco preguntas. Una recompensa de Halloween. Desbloquea un 5 %, 10 % o 15 % de descuento en tu entrada.',
    howTitle: 'Cómo funciona',
    r1: 'Recibes cinco preguntas de un tema asignado al azar: películas de Halloween, datos de Halloween, acertijos de hechizos o Baila Dembow y música.',
    r2: 'Cada respuesta correcta vale un punto. Tu descuento depende de tu puntuación:',
    tier5: '0–2 aciertos',
    tier10: '3–4 aciertos',
    tier15: '5 aciertos',
    tierResult: '{tier} % de descuento',
    r3: 'Primero juega. Cuando veas tu puntuación, introduce tus datos y confirma tu email para recibir el código. Un código por dirección de email.',
    r4: 'Las respuestas se bloquean y se puntúan en nuestro servidor. Las respuestas correctas no se muestran durante el quiz.',
    r5: 'Tu código de descuento se envía solo por email. Nunca se muestra en esta página.',
    r5b: 'El código es válido hasta el {date}.',
    r5c: 'Cada código es válido durante {hours} horas desde su envío, así que úsalo pronto.',
    r6: 'Debes tener 18 años o más. Los emails promocionales son opcionales y no hacen falta para participar ni para recibir tu descuento.',
    r7: 'Distintas direcciones de email pueden pertenecer a la misma persona; la regla de un intento se aplica por dirección de email verificada.',
    cta: 'Empezar el reto',
    starting: 'Repartiendo tus preguntas…',
    time: 'Dura unos dos minutos.',
    terms: 'Condiciones del evento',
  },
  form: {
    title: '¿Quién juega?',
    why: 'Usamos estos datos para gestionar el quiz, enviarte tu recompensa y entender quién participa. Lee el aviso de privacidad para ver el detalle.',
    fullName: 'Nombre completo',
    email: 'Dirección de email',
    age: 'Edad',
    ageHelp: 'En años cumplidos. Debes tener 18 años o más.',
    gender: 'Sexo / género',
    genderPick: 'Selecciona una opción',
    genderSelf: 'Descríbelo',
    city: 'Ciudad',
    country: 'País',
    countryPick: 'Selecciona tu país',
    accept: 'Acepto las reglas del quiz y he leído el {privacy}.',
    privacy: 'aviso de privacidad',
    terms: 'condiciones del evento',
    marketingLegend: 'Opcional',
    submit: 'Enviadme mi código',
    sending: 'Enviando…',
    summary: 'Corrige los campos marcados.',
    genders: { woman: 'Mujer', man: 'Hombre', non_binary: 'No binario', self_describe: 'Me describo yo mismo/a', prefer_not_to_say: 'Prefiero no decirlo' },
    err: {
      required: 'Rellena este campo.',
      invalid: 'Esto no parece correcto.',
      under_18: 'Debes tener 18 años o más para participar.',
      acceptTerms: 'Acepta para continuar.',
    },
  },
  verify: {
    title: 'Revisa tu bandeja de entrada',
    body: 'Hemos enviado un enlace y un código de 6 dígitos a {email}. Abre el enlace o introduce el código abajo.',
    hint: '¿No lo ves? Mira en spam. El enlace y el código funcionan una vez y caducan en {minutes} minutos.',
    codeLabel: 'Código de 6 dígitos',
    submit: 'Verificar email',
    verifying: 'Verificando…',
    resend: 'Enviar un email nuevo',
    resent: 'Hemos enviado un email nuevo.',
    edit: '¿Dirección equivocada? Editar mis datos',
    linkTitle: 'Verificando tu email…',
    linkFailed: 'Ese enlace no es válido o ha caducado.',
    linkFailedHelp: 'Escribe tu dirección de email y te enviaremos uno nuevo.',
    emailLabel: 'Dirección de email',
  },
  ready: {
    title: 'Email confirmado',
    body: 'Tus cinco preguntas están listas. Una vez bloqueada una respuesta, no se puede cambiar.',
    cta: 'Empezar el quiz',
  },
  quiz: {
    progress: 'Pregunta {n} de {total}',
    progressLabel: 'Progreso del quiz',
    theme: 'Tema',
    themes: { movies: 'Películas de Halloween', facts: 'Datos de Halloween', spells: 'Acertijos de hechizos', music: 'Baila Dembow y música' },
    lock: 'Bloquear mi respuesta',
    pick: 'Elige una respuesta para continuar',
    saving: 'Guardando tu respuesta…',
    resumed: 'Qué bueno verte de nuevo. Tu progreso está guardado.',
    answerGroup: 'Respuestas',
  },
  result: {
    title: 'Quiz completado',
    claimKicker: 'Tu recompensa',
    claimTitle: 'Consigue tu código del {tier} %',
    claimIntro: 'Introduce tus datos y confirma tu email. El código llega a esa dirección en menos de un minuto.',
    pendingTitle: 'Un paso más',
    pendingBody: 'Hemos enviado un enlace y un código de 6 dígitos a {email}. Abre el enlace o introduce el código abajo y tu código de descuento llegará enseguida.',
    alreadyTitle: 'Ya tienes un código',
    score: 'Has acertado {score} de {total}',
    earned: 'Has ganado un {tier} % de descuento en tu entrada de Halloween',
    codeNote: 'Nunca mostramos tu código en esta página. Se envía solo por email.',
    valid: 'Válido hasta el {date} (hora de Ámsterdam). Las restricciones figuran en el email.',
    validPersonal: 'Tu código caduca el {date} (hora de Ámsterdam). Úsalo antes.',
    emailTitle: 'Tu código',
    mail: {
      none: 'Estamos preparando tu email.',
      queued: 'Estamos enviando tu código. Puede tardar un minuto. Si falla, lo reintentamos automáticamente.',
      sending: 'Estamos enviando tu código.',
      accepted: 'Hemos entregado tu email a nuestro proveedor de correo. Revisa tu bandeja de entrada y el spam.',
      delivered: 'Entregado. Revisa tu bandeja de entrada y, si no lo ves, la carpeta de spam.',
      delayed: 'La entrega va con retraso. Debería llegar pronto.',
      bounced: 'El email ha rebotado. Usa el botón de abajo para enviarlo otra vez. Si sigue fallando, el equipo ya está avisado.',
      complained: 'El email se marcó como spam. Usa el botón de abajo para enviarlo otra vez.',
      failed: 'Aún no hemos podido enviar el email. Tu recompensa está a salvo. Usa el botón de abajo para intentarlo de nuevo.',
      waiting_for_inventory: 'Tu recompensa está reservada. Estamos reponiendo los códigos de tu nivel y tu email llegará en cuanto el tuyo esté listo. No hace falta repetir el quiz.',
    },
    resend: 'Enviar mi código otra vez',
    resendDone: 'Hemos enviado el mismo código otra vez.',
    resendWait: 'Espera un poco antes de volver a pedirlo.',
    tickets: 'Abrir la tienda de entradas',
    ticketsHelp: 'Usa el enlace de tu email para que el código se aplique al pagar.',
    already: 'Este email ya completó el quiz. Puedes pedir de nuevo el email con tu recompensa, pero el quiz no se puede repetir.',
    review: 'Tus respuestas',
    reviewCorrect: 'Respuesta correcta',
    reviewYours: 'Tu respuesta',
  },
  closed: {
    disabled: 'El quiz no está disponible en este momento.',
    not_open: 'El quiz abre el {date}.',
    closed: 'Esta campaña ha terminado. Gracias por jugar.',
    tickets: 'Ver el evento de Halloween',
  },
  music: { play: 'Reproducir música', mute: 'Silenciar música', unmute: 'Activar música', label: 'Música' },
  errors: {
    network: 'No hemos podido conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.',
    server: 'Algo ha fallado por nuestra parte. Inténtalo de nuevo en un momento.',
    not_configured: 'El quiz no está disponible temporalmente. Inténtalo más tarde.',
    rate_limited: 'Demasiados intentos. Inténtalo de nuevo en {minutes} min.',
    invalid_or_expired: 'Ese código o enlace no es válido o ha caducado. Envía un email nuevo e inténtalo otra vez.',
    email_unavailable: 'No hemos podido enviar el email ahora mismo. Inténtalo de nuevo en un momento.',
    no_session: 'Tu sesión ha terminado. Verifica tu email de nuevo.',
    campaign_unavailable: 'El quiz no está disponible en este momento.',
    question_bank_incomplete: 'El quiz aún no está listo. Inténtalo más tarde.',
    generic: 'Algo ha salido mal. Inténtalo de nuevo.',
  },
  footer: { terms: 'Condiciones', privacy: 'Privacidad', cookies: 'Ajustes de cookies', hello: 'Volver al evento de Halloween' },
  unsub: { title: 'Darse de baja', body: 'Confirma que ya no quieres recibir emails promocionales de Baila Dembow.', cta: 'Darme de baja', done: 'Te has dado de baja. No recibirás emails promocionales.', failed: 'Este enlace de baja no es válido.' },
};

const nl = {
  brandLine: 'Latin Halloween Festival',
  skipToContent: 'Ga naar de inhoud',
  loading: 'Laden…',
  retry: 'Probeer opnieuw',
  back: 'Terug',
  languageLabel: 'Taal',
  lang: {
    title: 'Kies je taal',
    intro: 'Je kunt altijd wisselen zonder je voortgang te verliezen.',
    suggested: 'Voorgesteld voor je browser',
    continue: 'Verder in {name}',
  },
  rules: {
    kicker: 'Baila Dembow Halloween-quiz',
    headline: 'Vijf vragen. Eén Halloween-beloning. Ontgrendel 5%, 10% of 15% korting op je ticket.',
    howTitle: 'Zo werkt het',
    r1: 'Je krijgt vijf vragen uit één willekeurig toegewezen thema: Halloween-films, Halloween-weetjes, spreukpuzzels of Baila Dembow en muziek.',
    r2: 'Elk goed antwoord is één punt. Je korting hangt af van je score:',
    tier5: '0–2 goed',
    tier10: '3–4 goed',
    tier15: '5 goed',
    tierResult: '{tier}% korting',
    r3: 'Speel eerst. Zie je je score, vul dan je gegevens in en bevestig je e-mail om de code te ontvangen. Eén code per e-mailadres.',
    r4: 'Antwoorden worden vergrendeld en op onze server beoordeeld. Goede antwoorden zie je tijdens de quiz niet.',
    r5: 'Je kortingscode krijg je alleen per e-mail. Hij wordt nooit op deze pagina getoond.',
    r5b: 'De code is geldig tot {date}.',
    r5c: 'Elke code is {hours} uur geldig nadat hij is verstuurd, dus gebruik hem snel.',
    r6: 'Je moet 18 jaar of ouder zijn. Reclamemails zijn optioneel en niet nodig om mee te doen of je korting te krijgen.',
    r7: 'Verschillende e-mailadressen kunnen toch bij dezelfde persoon horen; de regel van één poging geldt per geverifieerd e-mailadres.',
    cta: 'Start de uitdaging',
    starting: 'Je vragen worden gedeeld…',
    time: 'Duurt ongeveer twee minuten.',
    terms: 'Evenementvoorwaarden',
  },
  form: {
    title: 'Wie speelt er mee?',
    why: 'We gebruiken deze gegevens om de quiz uit te voeren, je beloning te sturen en te begrijpen wie meedoet. Lees de privacyverklaring voor alle details.',
    fullName: 'Volledige naam',
    email: 'E-mailadres',
    age: 'Leeftijd',
    ageHelp: 'In hele jaren. Je moet 18 jaar of ouder zijn.',
    gender: 'Geslacht / gender',
    genderPick: 'Kies een optie',
    genderSelf: 'Omschrijf',
    city: 'Stad',
    country: 'Land',
    countryPick: 'Kies je land',
    accept: 'Ik accepteer de quizregels en heb de {privacy} gelezen.',
    privacy: 'privacyverklaring',
    terms: 'evenementvoorwaarden',
    marketingLegend: 'Optioneel',
    submit: 'Stuur mij mijn code',
    sending: 'Versturen…',
    summary: 'Corrigeer de gemarkeerde velden.',
    genders: { woman: 'Vrouw', man: 'Man', non_binary: 'Non-binair', self_describe: 'Ik omschrijf het zelf', prefer_not_to_say: 'Zeg ik liever niet' },
    err: {
      required: 'Vul dit in.',
      invalid: 'Dit klopt niet helemaal.',
      under_18: 'Je moet 18 jaar of ouder zijn om mee te doen.',
      acceptTerms: 'Accepteer om door te gaan.',
    },
  },
  verify: {
    title: 'Check je inbox',
    body: 'We hebben een link en een code van 6 cijfers gestuurd naar {email}. Open de link of vul hieronder de code in.',
    hint: 'Geen mail? Kijk in je spam. De link en code werken één keer en verlopen na {minutes} minuten.',
    codeLabel: 'Code van 6 cijfers',
    submit: 'E-mail verifiëren',
    verifying: 'Verifiëren…',
    resend: 'Stuur een nieuwe e-mail',
    resent: 'Er is een nieuwe e-mail onderweg.',
    edit: 'Verkeerd adres? Mijn gegevens aanpassen',
    linkTitle: 'Je e-mail wordt geverifieerd…',
    linkFailed: 'Deze link is ongeldig of verlopen.',
    linkFailedHelp: 'Vul je e-mailadres in en we sturen een nieuwe.',
    emailLabel: 'E-mailadres',
  },
  ready: {
    title: 'E-mail bevestigd',
    body: 'Je vijf vragen staan klaar. Een vergrendeld antwoord kun je niet meer wijzigen.',
    cta: 'Start de quiz',
  },
  quiz: {
    progress: 'Vraag {n} van {total}',
    progressLabel: 'Voortgang van de quiz',
    theme: 'Thema',
    themes: { movies: 'Halloween-films', facts: 'Halloween-weetjes', spells: 'Spreukpuzzels', music: 'Baila Dembow en muziek' },
    lock: 'Mijn antwoord vergrendelen',
    pick: 'Kies een antwoord om door te gaan',
    saving: 'Je antwoord wordt opgeslagen…',
    resumed: 'Welkom terug. Je voortgang is bewaard.',
    answerGroup: 'Antwoorden',
  },
  result: {
    title: 'Quiz voltooid',
    claimKicker: 'Je beloning',
    claimTitle: 'Ontvang je {tier}%-code',
    claimIntro: 'Vul je gegevens in en bevestig je e-mail. De code komt binnen een minuut op dat adres aan.',
    pendingTitle: 'Nog één stap',
    pendingBody: 'We hebben een link en een code van 6 cijfers gestuurd naar {email}. Open de link of vul hieronder de code in; je kortingscode volgt direct.',
    alreadyTitle: 'Je hebt al een code',
    score: 'Je had {score} van de {total} goed',
    earned: 'Je hebt {tier}% korting op je Halloween-ticket verdiend',
    codeNote: 'We tonen je code nooit op deze pagina. Je krijgt hem alleen per e-mail.',
    valid: 'Geldig tot {date} (Amsterdamse tijd). Beperkingen staan in de e-mail.',
    validPersonal: 'Je code verloopt op {date} (Amsterdamse tijd). Gebruik hem vóór die tijd.',
    emailTitle: 'Je code',
    mail: {
      none: 'We bereiden je e-mail voor.',
      queued: 'We versturen je code. Dat kan even duren. Mislukt het, dan proberen we het automatisch opnieuw.',
      sending: 'We versturen je code.',
      accepted: 'Je e-mail is overgedragen aan onze e-mailprovider. Check je inbox en je spamfolder.',
      delivered: 'Bezorgd. Check je inbox, en je spamfolder als je hem niet ziet.',
      delayed: 'De bezorging loopt vertraging op. Hij zou snel moeten aankomen.',
      bounced: 'De e-mail is teruggekaatst. Gebruik de knop hieronder om hem opnieuw te sturen. Blijft het mislukken, dan is het team gewaarschuwd.',
      complained: 'De e-mail is als spam gemarkeerd. Gebruik de knop hieronder om hem opnieuw te sturen.',
      failed: 'We konden de e-mail nog niet versturen. Je beloning is veilig. Gebruik de knop hieronder om het opnieuw te proberen.',
      waiting_for_inventory: 'Je beloning is gereserveerd. De codes voor jouw niveau worden aangevuld en je e-mail volgt zodra die van jou klaar is. Je hoeft de quiz niet opnieuw te doen.',
    },
    resend: 'Stuur mijn code opnieuw',
    resendDone: 'We hebben dezelfde code opnieuw gestuurd.',
    resendWait: 'Wacht even voordat je het opnieuw vraagt.',
    tickets: 'Open de ticketshop',
    ticketsHelp: 'Gebruik de link in je e-mail zodat de code bij het afrekenen wordt toegepast.',
    already: 'Met dit e-mailadres is de quiz al voltooid. Je kunt de beloningsmail opnieuw aanvragen, maar de quiz kan niet opnieuw worden gespeeld.',
    review: 'Je antwoorden',
    reviewCorrect: 'Goede antwoord',
    reviewYours: 'Jouw antwoord',
  },
  closed: {
    disabled: 'De quiz is op dit moment niet beschikbaar.',
    not_open: 'De quiz opent op {date}.',
    closed: 'Deze actie is afgelopen. Bedankt voor het meespelen.',
    tickets: 'Bekijk het Halloween-evenement',
  },
  music: { play: 'Muziek afspelen', mute: 'Muziek dempen', unmute: 'Muziek aanzetten', label: 'Muziek' },
  errors: {
    network: 'We konden de server niet bereiken. Controleer je verbinding en probeer het opnieuw.',
    server: 'Er ging iets mis aan onze kant. Probeer het zo opnieuw.',
    not_configured: 'De quiz is tijdelijk niet beschikbaar. Probeer het later opnieuw.',
    rate_limited: 'Te veel pogingen. Probeer het over {minutes} min opnieuw.',
    invalid_or_expired: 'Die code of link is ongeldig of verlopen. Stuur een nieuwe e-mail en probeer opnieuw.',
    email_unavailable: 'We konden de e-mail nu niet versturen. Probeer het zo opnieuw.',
    no_session: 'Je sessie is verlopen. Verifieer je e-mail opnieuw.',
    campaign_unavailable: 'De quiz is op dit moment niet beschikbaar.',
    question_bank_incomplete: 'De quiz is nog niet klaar. Probeer het later opnieuw.',
    generic: 'Er ging iets mis. Probeer het opnieuw.',
  },
  footer: { terms: 'Voorwaarden', privacy: 'Privacy', cookies: 'Cookie-instellingen', hello: 'Terug naar het Halloween-evenement' },
  unsub: { title: 'Uitschrijven', body: 'Bevestig dat je geen reclamemails van Baila Dembow meer wilt ontvangen.', cta: 'Schrijf me uit', done: 'Je bent uitgeschreven. Je ontvangt geen reclamemails meer.', failed: 'Deze uitschrijflink is niet geldig.' },
};

const DICT = { en, es, nl };

const lookup = (obj, key) => key.split('.').reduce((o, k) => (o && o[k] !== undefined ? o[k] : undefined), obj);

export const makeT = (lang) => (key, vars = {}) => {
  let s = lookup(DICT[lang], key);
  if (s === undefined) s = lookup(DICT.en, key);
  if (typeof s !== 'string') return key;
  return s.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));
};

export const fmtDate = (iso, lang) =>
  iso
    ? new Intl.DateTimeFormat(LOCALES[lang] || 'en-GB', { dateStyle: 'long', timeStyle: 'short', timeZone: 'Europe/Amsterdam' }).format(new Date(iso))
    : '';

// First of the browser's preferred languages that we support.
export const suggestLanguage = () => {
  try {
    const prefs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
    for (const p of prefs) {
      const base = String(p || '').toLowerCase().split('-')[0];
      if (LANGS.includes(base)) return base;
    }
  } catch (_) { /* ignore */ }
  return 'en';
};
